import { useCallback, useEffect, useId, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  LuArrowDown,
  LuArrowUp,
  LuBookOpen,
  LuCalendarCheck,
  LuCircle,
  LuCircleCheck,
  LuCircleDashed,
  LuCornerDownLeft,
  LuListChecks,
  LuLogOut,
  LuMonitor,
  LuMoon,
  LuNewspaper,
  LuPenLine,
  LuSearch,
  LuShuffle,
  LuSun,
  LuTrophy,
  LuUser,
} from 'react-icons/lu';
import { usePalette } from './paletteStore';
import { useSignOut } from './useSignOut';
import { DifficultyLabel } from '../ui/Labels';
import { useToast } from '../ui/Toast';
import { useStrings } from '../tenant/TenantProvider';
import { useFeatureEnabled } from '../tenant/useTenantSettings';
import { useCanWritePosts } from '../features/blog/permissions';
import { useTheme } from '../theme/ThemeProvider';
import { useCatalog } from '../stores/catalog';

const GROUP_ORDER_BROWSE = ['pages', 'actions', 'problems', 'contests'];
const GROUP_ORDER_SEARCH = ['problems', 'contests', 'pages', 'actions'];
const GROUP_LIMIT = { problems: 8, contests: 5 };
const SUGGESTED_PROBLEMS = 5;
const STATUS_ICON = { solved: LuCircleCheck, attempted: LuCircleDashed };
const THEME_OPTIONS = [
  { value: 'light', icon: LuSun },
  { value: 'dark', icon: LuMoon },
  { value: 'system', icon: LuMonitor },
];

function score(item, query) {
  if (!query) return 1;
  const label = item.label.toLowerCase();
  const index = label.indexOf(query);
  if (index === 0) return 100 - label.length / 100;
  if (index > 0) return (label[index - 1] === ' ' ? 80 : 60) - index / 100;
  if (item.keywords?.some((keyword) => keyword === query)) return 90;
  if (item.keywords?.some((keyword) => keyword.includes(query))) return 40;
  return 0;
}

function Highlight({ text, query }) {
  const index = query ? text.toLowerCase().indexOf(query) : -1;
  if (index < 0) return text;
  return (
    <>
      {text.slice(0, index)}
      <mark>{text.slice(index, index + query.length)}</mark>
      {text.slice(index + query.length)}
    </>
  );
}

function useGlobalShortcut(toggle) {
  useEffect(() => {
    const onKeyDown = (event) => {
      if (!(event.metaKey || event.ctrlKey) || event.altKey || event.key.toLowerCase() !== 'k') return;
      if (event.target instanceof Element && event.target.closest('.monaco-editor, [data-palette-ignore]')) return;
      event.preventDefault();
      toggle();
    };
    window.addEventListener('keydown', onKeyDown);
    return () => window.removeEventListener('keydown', onKeyDown);
  }, [toggle]);
}

function PaletteDialog({ onClose }) {
  const t = useStrings();
  const navigate = useNavigate();
  const { show } = useToast();
  const { preference, setPreference } = useTheme();
  const signOut = useSignOut();
  const problems = useCatalog((state) => state.problems.data);
  const contests = useCatalog((state) => state.contests.data);
  const today = useCatalog((state) => state.daily.data?.today);
  const blogsEnabled = useFeatureEnabled('blogs');
  const canWritePosts = useCanWritePosts() === true;
  const [query, setQuery] = useState('');
  const [activeIndex, setActiveIndex] = useState(0);
  const inputRef = useRef(null);
  const listRef = useRef(null);
  const listId = useId();
  const normalized = query.trim().toLowerCase();

  useEffect(() => {
    const { loadProblems, loadContests, problems: loaded } = useCatalog.getState();
    if (!loaded.data) loadProblems();
    if (!useCatalog.getState().contests.data) loadContests();
    inputRef.current?.focus();
  }, []);

  const go = useCallback(
    (path) => {
      onClose(false);
      navigate(path, { viewTransition: true });
    },
    [navigate, onClose]
  );

  const items = useMemo(() => {
    const pages = [
      { id: 'page-problems', group: 'pages', icon: LuListChecks, label: t('nav.problems'), run: () => go('/problems') },
      { id: 'page-contests', group: 'pages', icon: LuTrophy, label: t('nav.contests'), run: () => go('/contests') },
      { id: 'page-courses', group: 'pages', icon: LuBookOpen, label: t('nav.courses'), run: () => go('/courses') },
      blogsEnabled && { id: 'page-blog', group: 'pages', icon: LuNewspaper, label: t('nav.blog'), run: () => go('/blog') },
      { id: 'page-profile', group: 'pages', icon: LuUser, label: t('nav.profile'), run: () => go('/profile') },
    ].filter(Boolean);

    const unsolved = (problems ?? []).filter((problem) => problem.status !== 'solved');
    const actions = [
      today && {
        id: 'action-today',
        group: 'actions',
        icon: LuCalendarCheck,
        label: t('palette.today', { title: today.title }),
        run: () => go(`/problems/${today.questionId}`),
      },
      blogsEnabled && canWritePosts && {
        id: 'action-write-post',
        group: 'actions',
        icon: LuPenLine,
        label: t('palette.writePost'),
        keywords: ['blog', 'post', 'write', 'article'],
        run: () => go('/blog/new'),
      },
      {
        id: 'action-random',
        group: 'actions',
        icon: LuShuffle,
        label: t('palette.random'),
        run: () => {
          if (unsolved.length === 0) {
            onClose(false);
            show(t('problems.pickRandomNone'), 'error');
            return;
          }
          go(`/problems/${unsolved[Math.floor(Math.random() * unsolved.length)].id}`);
        },
      },
      ...THEME_OPTIONS.filter((option) => option.value !== preference).map((option) => ({
        id: `action-theme-${option.value}`,
        group: 'actions',
        icon: option.icon,
        label: t(`palette.theme.${option.value}`),
        run: () => {
          setPreference(option.value);
          onClose();
        },
      })),
      {
        id: 'action-sign-out',
        group: 'actions',
        icon: LuLogOut,
        label: t('nav.signOut'),
        run: () => {
          onClose(false);
          signOut();
        },
      },
    ].filter(Boolean);

    const problemItems = (problems ?? []).map((problem) => ({
      id: `problem-${problem.id}`,
      group: 'problems',
      icon: STATUS_ICON[problem.status] ?? LuCircle,
      tone: problem.status,
      label: problem.title,
      number: problem.id,
      keywords: [String(problem.id), ...problem.tags.map((tag) => tag.toLowerCase())],
      meta: <DifficultyLabel level={problem.difficulty} />,
      suggested: problem.status !== 'solved',
      run: () => go(`/problems/${problem.id}`),
    }));

    const contestItems = (contests ?? [])
      .filter((contest) => contest.state !== 'draft' && contest.state !== 'cancelled')
      .map((contest) => ({
        id: `contest-${contest.id}`,
        group: 'contests',
        icon: LuTrophy,
        label: contest.name,
        meta: t(`contests.states.${contest.state}`),
        suggested: contest.state === 'live' || contest.state === 'upcoming',
        run: () => go(contest.state === 'live' && contest.registered ? `/contests/${contest.id}/arena` : `/contests/${contest.id}`),
      }));

    const groups = { pages, actions, problems: problemItems, contests: contestItems };
    const order = normalized ? GROUP_ORDER_SEARCH : GROUP_ORDER_BROWSE;
    return order.flatMap((group) => {
      if (!normalized) {
        const list = groups[group];
        return group === 'problems' ? list.filter((item) => item.suggested).slice(0, SUGGESTED_PROBLEMS) : group === 'contests' ? list.filter((item) => item.suggested) : list;
      }
      return groups[group]
        .map((item) => ({ item, rank: score(item, normalized) }))
        .filter((entry) => entry.rank > 0)
        .sort((a, b) => b.rank - a.rank)
        .slice(0, GROUP_LIMIT[group] ?? 6)
        .map((entry) => entry.item);
    });
  }, [t, go, onClose, show, problems, contests, today, blogsEnabled, canWritePosts, preference, setPreference, signOut, normalized]);

  useEffect(() => {
    setActiveIndex(0);
  }, [normalized]);

  useEffect(() => {
    listRef.current?.querySelector('[aria-selected="true"]')?.scrollIntoView({ block: 'nearest' });
  }, [activeIndex]);

  const onKeyDown = (event) => {
    if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
      event.preventDefault();
      if (items.length === 0) return;
      const step = event.key === 'ArrowDown' ? 1 : -1;
      setActiveIndex((index) => (index + step + items.length) % items.length);
    } else if (event.key === 'Enter') {
      event.preventDefault();
      items[activeIndex]?.run();
    } else if (event.key === 'Escape') {
      event.preventDefault();
      onClose();
    } else if (event.key === 'Tab') {
      event.preventDefault();
    }
  };

  let lastGroup = null;
  const activeId = items[activeIndex] ? `${listId}-${items[activeIndex].id}` : undefined;

  return (
    <div
      className="palette-backdrop"
      onMouseDown={(event) => {
        if (event.target === event.currentTarget) onClose();
      }}
    >
      <div className="palette" role="dialog" aria-modal="true" aria-label={t('palette.label')}>
        <div className="palette__search">
          <LuSearch aria-hidden="true" />
          <input
            ref={inputRef}
            className="palette__input"
            role="combobox"
            aria-expanded="true"
            aria-controls={listId}
            aria-activedescendant={activeId}
            aria-autocomplete="list"
            autoComplete="off"
            spellCheck="false"
            placeholder={t('palette.placeholder')}
            value={query}
            onChange={(event) => setQuery(event.target.value)}
            onKeyDown={onKeyDown}
          />
          <kbd className="kbd">{t('palette.escape')}</kbd>
        </div>
        <div ref={listRef} id={listId} role="listbox" aria-label={t('palette.label')} className="palette__list">
          {items.length === 0 && <p className="palette__empty">{t('palette.empty', { query: query.trim() })}</p>}
          {items.map((item, index) => {
            const heading = item.group !== lastGroup ? t(`palette.groups.${item.group}`) : null;
            lastGroup = item.group;
            const Icon = item.icon;
            return (
              <div key={item.id} role="presentation">
                {heading && (
                  <p className="palette__group" role="presentation">
                    {heading}
                  </p>
                )}
                <div
                  id={`${listId}-${item.id}`}
                  role="option"
                  aria-selected={index === activeIndex}
                  className={`palette__item${item.tone ? ` palette__item--${item.tone}` : ''}`}
                  onMouseMove={() => index !== activeIndex && setActiveIndex(index)}
                  onClick={() => item.run()}
                >
                  <Icon aria-hidden="true" />
                  {item.number !== undefined && <span className="palette__item-number tabular">{item.number}</span>}
                  <span className="palette__item-label">
                    <Highlight text={item.label} query={normalized} />
                  </span>
                  {item.meta && <span className="palette__item-meta">{item.meta}</span>}
                </div>
              </div>
            );
          })}
        </div>
        <div className="palette__footer" aria-hidden="true">
          <span>
            <kbd className="kbd">
              <LuArrowUp />
            </kbd>
            <kbd className="kbd">
              <LuArrowDown />
            </kbd>
            {t('palette.hintNavigate')}
          </span>
          <span>
            <kbd className="kbd">
              <LuCornerDownLeft />
            </kbd>
            {t('palette.hintOpen')}
          </span>
        </div>
      </div>
    </div>
  );
}

export function CommandPalette() {
  const open = usePalette((state) => state.open);
  const setOpen = usePalette((state) => state.setOpen);
  const toggle = usePalette((state) => state.toggle);
  const returnFocusRef = useRef(null);

  useGlobalShortcut(toggle);

  useLayoutEffect(() => {
    if (open) returnFocusRef.current = document.activeElement;
  }, [open]);

  const close = useCallback(
    (restoreFocus = true) => {
      setOpen(false);
      const target = returnFocusRef.current;
      if (restoreFocus && target instanceof HTMLElement && target.isConnected) target.focus();
    },
    [setOpen]
  );

  return open ? <PaletteDialog onClose={close} /> : null;
}
